<script lang="ts">
  import type { SkillId } from '@rsvjelle/game-core';

  interface Props {
    step: number;
    skill: SkillId;
    onAdvance: () => void;
    onComplete: () => void;
  }
  let { step, skill, onAdvance, onComplete }: Props = $props();
  let panelLeft = $state<number | null>(null);
  let panelTop = $state<number | null>(null);
  let dragging = $state(false);
  let dragStartX = 0;
  let dragStartY = 0;
  let panelStartX = 0;
  let panelStartY = 0;

  let pages = $derived([
    { icon: '✦', eyebrow: 'WELCOME, REALMWALKER', title: 'Build a realm that never sleeps', copy: 'Skills train continuously and keep gathering while you are away. Let’s make your first decisions inside the real game.', target: '' },
    { icon: '🧭', eyebrow: 'CHOOSE A PATH', title: `Begin with ${skill}`, copy: 'You can change skills at any time. Every skill produces its own resource and all of them continue shaping the same realm.', target: `[data-onboarding-skill="${skill}"]` },
    { icon: '⚡', eyebrow: 'YOUR FIRST ACTION', title: `Train ${skill} now`, copy: 'Tap the highlighted button for an instant XP and resource burst. Automatic production remains the heart of your realm.', target: '[data-onboarding="train"]' },
    { icon: '📜', eyebrow: 'GOALS AND RARE FINDS', title: 'Open Contracts', copy: 'Contracts provide extra goals, while Rifts can reveal permanent Artifacts. Let’s look at where their rewards are stored.', target: '[data-onboarding="contracts-tab"]' },
    { icon: '🎁', eyebrow: 'ASCENSION-SAFE REWARDS', title: 'Contracts survive every reset', copy: 'Completed rewards wait in your Vault until you activate them. Gold, Hall supplies and timed boosts can all be saved for the moment they matter most.', target: '', button: 'Continue' },
    { icon: '🛠️', eyebrow: 'TURN PROGRESS INTO POWER', title: 'Open Upgrades', copy: 'This is where resources become gold and where each skill’s Tool and Hall are improved.', target: '[data-onboarding="upgrades-tab"]' },
    { icon: '🏛️', eyebrow: 'TOOLS, HALLS AND SUPPLY ROUTES', title: 'Build the skill you are viewing', copy: 'Tools cost gold and unlock every five skill levels. Halls consume resources made by two other skills, so switching paths is essential.', target: '', button: 'Continue' },
    { icon: '🌌', eyebrow: 'THE LONG GAME', title: 'Open Ascension', copy: 'Ascension becomes your answer when the current run starts slowing down.', target: '[data-onboarding="ascension-tab"]' },
    { icon: '✦', eyebrow: 'RESET TO RETURN STRONGER', title: 'Ascension creates permanent power', copy: 'A reset trades run progress for Echo Shards. Permanent upgrades, achievements, Artifacts and Vault rewards remain; later Ascensions unlock Doctrines and automation.', target: '', button: 'Continue' },
    { icon: '🛡️', eyebrow: 'PROTECT YOUR REALM', title: 'Create an account for cloud sync', copy: 'Local play works without an account, but signing in through More protects your progress and lets you continue on another browser or Android device. Milestones and achievements are also found there.', target: '[data-onboarding="more-tab"]' }
  ]);

  $effect(() => {
    const selector = pages[step]?.target;
    let target: Element | null = null;
    const timer = setTimeout(() => {
      if (!selector) return;
      target = document.querySelector(selector);
      target?.classList.add('onboarding-highlight');
      if (target && !selector.includes('-tab')) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);
    return () => {
      clearTimeout(timer);
      target?.classList.remove('onboarding-highlight');
    };
  });

  function startDrag(event: PointerEvent) {
    if (event.button !== 0) return;
    const handle = event.currentTarget as HTMLElement;
    const panel = handle.closest('.onboarding-guide') as HTMLElement | null;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    panelLeft = rect.left;
    panelTop = rect.top;
    panelStartX = rect.left;
    panelStartY = rect.top;
    dragStartX = event.clientX;
    dragStartY = event.clientY;
    dragging = true;
    handle.setPointerCapture(event.pointerId);
    event.preventDefault();
  }

  function moveDrag(event: PointerEvent) {
    if (!dragging) return;
    const panel = (event.currentTarget as HTMLElement).closest('.onboarding-guide') as HTMLElement | null;
    if (!panel) return;
    const margin = 8;
    const maxLeft = Math.max(margin, window.innerWidth - panel.offsetWidth - margin);
    const maxTop = Math.max(margin, window.innerHeight - panel.offsetHeight - margin);
    panelLeft = Math.min(maxLeft, Math.max(margin, panelStartX + event.clientX - dragStartX));
    panelTop = Math.min(maxTop, Math.max(margin, panelStartY + event.clientY - dragStartY));
  }

  function stopDrag() {
    dragging = false;
  }
</script>

<aside class:nav-step={step === 3 || step === 5 || step === 7} class:dragging class="onboarding-guide" style={panelLeft === null ? '' : `left:${panelLeft}px;top:${panelTop}px;right:auto;bottom:auto`} aria-live="polite" aria-label={`Introduction step ${step + 1} of ${pages.length}`}>
  <div class="onboarding-drag-handle" role="button" tabindex="0" aria-label="Move introduction" title="Drag to move" onpointerdown={startDrag} onpointermove={moveDrag} onpointerup={stopDrag} onpointercancel={stopDrag}>
    <span class="drag-grip" aria-hidden="true">⠿</span>
    <div class="onboarding-progress">{#each pages as _, index}<span class:active={index <= step}></span>{/each}</div>
  </div>
  <div class="onboarding-guide-copy">
    <span>{pages[step].icon}</span>
    <div><p class="eyebrow">{pages[step].eyebrow}</p><h2>{pages[step].title}</h2><small>{pages[step].copy}</small></div>
  </div>
  {#if step === pages.length - 1}<button type="button" onclick={onComplete}>Begin my journey</button>
  {:else if step === 0}<button type="button" onclick={onAdvance}>Show me</button>
  {:else if pages[step].button}<button type="button" onclick={onAdvance}>{pages[step].button}</button>
  {:else}<p class="onboarding-wait">Follow the highlighted action to continue</p>{/if}
  <button class="onboarding-skip" type="button" onclick={onComplete}>Skip</button>
</aside>
